import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileUp, Loader2, X, FileText, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPT = '.txt,.pdf,.json,.hl7,.xml,.md,text/plain,application/pdf,application/json';
const MAX_MB = 10;

/**
 * Iter 149 · Copy-From-EHR drop-zone.
 *
 * Planner drops a discharge summary file (PDF / text / FHIR JSON)
 * onto the panel; the widget POSTs to /api/matching/ehr/parse and
 * calls ``onExtracted(fields)`` with the sanitised referral fields.
 * The parent form decides how to hydrate — usually: fill any empty
 * field, leave planner-typed values alone.
 *
 * BAA-locked LLM path; PHI scrubbed server-side.
 *
 * Props:
 *   onExtracted(fields: object, source: {filename, size_bytes,...}): void
 *   disabled?: boolean
 */
export function EhrDropZone({ onExtracted, disabled }) {
  const { getAuthHeader } = useAuth();
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('max_codes', '6');
      const { data } = await axios.post(
        `${API}/matching/ehr/parse`,
        form,
        {
          ...getAuthHeader(),
          headers: {
            ...(getAuthHeader().headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setLastResult({
        filename: file.name,
        extracted: data.extracted || {},
        warnings: data.warnings || [],
        source: data.source,
      });
      onExtracted?.(data.extracted || {}, data.source);
      const filled = Object.entries(data.extracted || {}).filter(
        ([, v]) => v && (Array.isArray(v) ? v.length : true),
      ).length;
      if (filled === 0) {
        toast.info('No fields could be extracted from this file.');
      } else {
        toast.success(`Extracted ${filled} field${filled === 1 ? '' : 's'} from ${file.name}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'EHR parse failed';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [getAuthHeader, onExtracted]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  }, [handleFile, disabled, busy]);

  const onSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset so the same filename can be picked again
    e.target.value = '';
  };

  const clear = () => setLastResult(null);

  return (
    <div
      className={`rounded-xl border border-dashed transition-colors ${
        dragOver
          ? 'border-indigo-400 bg-indigo-500/10'
          : 'border-indigo-500/40 bg-indigo-500/5'
      }`}
      data-testid="ehr-dropzone"
      onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={onSelect}
        disabled={disabled || busy}
        data-testid="ehr-file-input"
      />

      <div className="p-4 flex items-start gap-3">
        <div className="rounded-lg bg-indigo-500/15 p-2 shrink-0">
          <FileUp className="h-5 w-5 text-indigo-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-indigo-200">
            <span className="font-medium">Copy from EHR</span>
            <span className="text-xs text-slate-500">
              Drop or pick a discharge summary — PDF, text, or FHIR JSON
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" />
            BAA-locked · PHI scrubbed · auto-fills empty fields only
          </div>

          {!lastResult && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || busy}
                data-testid="ehr-pick-file-btn"
              >
                {busy ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Parsing…</>
                ) : (
                  <><FileUp className="h-4 w-4 mr-1.5" /> Pick file</>
                )}
              </Button>
              <span className="text-xs text-slate-500">
                or drag it here · max {MAX_MB} MB
              </span>
            </div>
          )}

          {lastResult && (
            <div className="mt-3 space-y-2" data-testid="ehr-result">
              <div className="flex items-start justify-between rounded-lg bg-slate-950/60 border border-slate-800 p-3">
                <div className="flex items-start gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate" title={lastResult.filename}>
                      {lastResult.filename}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {lastResult.source?.text_length?.toLocaleString?.() || 0} chars ·
                      {' '}
                      {Object.values(lastResult.extracted).filter(v => v && (Array.isArray(v) ? v.length : true)).length}{' '}
                      field(s) hydrated
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clear}
                  className="text-slate-500 hover:text-slate-300"
                  aria-label="Dismiss"
                  data-testid="ehr-result-dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {lastResult.warnings.length > 0 && (
                <div className="text-xs text-amber-300 space-y-0.5">
                  {lastResult.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
